import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import api from '../../../Component/axios_Interceptor/api';
import url, { SERVER_ADDRESS } from '../../../Component/server';

type Props = { liveSeq: number; onDismiss: () => void; onSent: () => void };
type Preview = { recipient_count: number; estimated_coins: number; dispatch: { dispatch_id: number } | null };

export default function StartNotificationSheet({ liveSeq, onDismiss, onSent }: Props) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const active = useRef(true);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const previewUrl = `${SERVER_ADDRESS}notifications/preview/${liveSeq}`;

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setError('');
    setPreview(null);
    api.get(previewUrl, { params: { kind: 'start' } }).then(({ data }: { data: Preview }) => {
      if (cancelled) return;
      if (Number.isSafeInteger(data?.dispatch?.dispatch_id)) { onDismiss(); return; }
      if (!Number.isSafeInteger(data?.recipient_count) || data.recipient_count < 0
        || !Number.isSafeInteger(data?.estimated_coins) || data.estimated_coins < 0) throw new Error('Invalid preview');
      setPreview(data);
    }).catch(() => {
      if (!cancelled) setError('발송 정보를 불러오지 못했습니다. 다시 확인해주세요.');
    });
    return () => { cancelled = true; };
  }, [previewUrl, retry, onDismiss]);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const panel = panelRef.current;
    panel?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!sendingRef.current) onDismiss();
      }
      if (event.key !== 'Tab' || !panel) return;
      const buttons = Array.from(panel.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    panel?.addEventListener('keydown', onKeyDown);
    return () => {
      panel?.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [onDismiss]);

  const send = async () => {
    if (sendingRef.current || !preview || preview.recipient_count === 0) return;
    sendingRef.current = true;
    panelRef.current?.focus();
    setSending(true);
    setError('');
    try {
      const { data } = await api.post(url.Communication_url.send_alimtalk, { live_seq: liveSeq, retry_failed: false });
      if (!Number.isSafeInteger(data?.dispatch_id)) throw new Error('발송 접수 결과를 확인하지 못했습니다.');
      if (active.current) onSent();
    } catch (failure: any) {
      // A lost response can still mean the one-time dispatch was accepted.
      try {
        const { data } = await api.get(previewUrl, { params: { kind: 'start' } });
        if (Number.isSafeInteger(data?.dispatch?.dispatch_id)) {
          if (active.current) onSent();
          return;
        }
      } catch { /* The server still prevents a second dispatch. */ }
      if (active.current) {
        const message = failure?.response?.data?.message;
        setError(typeof message === 'string' ? message : '발송 결과를 확인하지 못했습니다. 관리자 화면에서 발송 내역을 확인해주세요.');
      }
    } finally {
      sendingRef.current = false;
      if (active.current) setSending(false);
    }
  };

  return createPortal(<Backdrop>
    <Sheet ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={sending}>
      <h2 id={titleId}>라이브 시작 알림톡을 보내시겠습니까?</h2>
      <div id={descriptionId}>
        <p>시작 알림톡은 이 라이브에서 한 번만 보낼 수 있습니다.</p>
        <p>나중에 보내려면 관리자 화면에서 직접 발송할 수 있습니다.</p>
      </div>
      {preview && <p className="estimate">대상 {preview.recipient_count.toLocaleString()}명 · 예상 사용 {preview.estimated_coins.toLocaleString()}코인</p>}
      {!preview && !error && <p className="estimate" role="status">발송 정보를 확인하고 있습니다.</p>}
      {preview?.recipient_count === 0 && <p className="estimate">현재 알림톡을 받을 대상이 없습니다.</p>}
      {error && <p className="error" role="alert">{error} {!preview && <button type="button" onClick={() => setRetry(value => value + 1)}>다시 확인</button>}</p>}
      <div className="actions">
        <button type="button" onClick={onDismiss} disabled={sending}>나중에 보내기</button>
        <button type="button" onClick={() => void send()} disabled={sending || !preview || preview.recipient_count === 0}>{sending ? '보내는 중…' : '지금 보내기'}</button>
      </div>
    </Sheet>
  </Backdrop>, document.body);
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: #0008;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 24px);
  max-height: calc(100dvh - 24px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: 28px 24px calc(20px + env(safe-area-inset-bottom));
  border-radius: 24px 24px 0 0;
  background: var(--admin-theme-surface, #211e29);
  color: var(--admin-theme-text, #fff);
  font-family: var(--font-family-base); font-weight: var(--font-weight-regular);
  text-align: left;
  word-break: keep-all;
  overflow-wrap: anywhere;
  h2 { margin: 0 0 16px; font: var(--font-weight-semibold) 20px/1.45 var(--font-family-base); }
  p { margin: 0; font-size: 15px; line-height: 1.65; }
  p + p { margin-top: 8px; }
  .estimate { margin-top: 16px; color: var(--admin-theme-muted, #c4bccf); font-size: 13px; }
  .error { margin-top: 16px; color: var(--admin-theme-danger, #ffaaaa); font-size: 14px; }
  .error button { display: block; min-height: 44px; padding: 8px 0; border: 0; background: none; color: inherit; font: inherit; text-decoration: underline; }
  .actions { display: flex; gap: 10px; margin-top: 24px; }
  .actions button {
    flex: 1;
    min-width: 0;
    min-height: 50px;
    padding: 12px 8px;
    border: 0;
    border-radius: 12px;
    background: var(--admin-theme-raised, #393340);
    color: var(--admin-theme-text, #fff);
    font: var(--font-weight-semibold) 15px/1.4 var(--font-family-base);
  }
  .actions button:last-child { background: var(--admin-accent, #6b3ee5); color: #fff; }
  button { cursor: pointer; }
  button:active:not(:disabled) { filter: brightness(.9); }
  button:disabled { opacity: .5; cursor: not-allowed; }
  button:focus-visible { outline: 3px solid var(--admin-theme-focus, #b69aff); outline-offset: 3px; }
`;
